import {
    EPrimitiveKind,
    ETextAlign,
    ETextBaseline,
    IArcPrimitive,
    IDimPrimitive,
    IHatchPrimitive,
    IPlanPrimitive,
    IPoint,
    IPolyPrimitive,
    ITextPrimitive,
} from '../plan-primitive';
import { IDxfRoom, detailEntities } from './dxf-dim-parts';
import { dimEntities } from './dxf-dims';
import {
    EDxfEntityKind,
    EDxfHatchPattern,
    EDxfHorizontalAlign,
    EDxfVerticalAlign,
    IDxfEntity,
    IDxfPoint,
    IDxfVertex,
} from './dxf-entities';
import { IDxfPaneFit, toPaneMm } from './dxf-fit';
import { EDxfLayerKind, paneLayerName } from './dxf-layers';
import { fillOutlineKind, isScreenExtensionLine, strokeKind } from './dxf-line-kind';
import { IDxfSketch, sketchOf } from './dxf-sketch';
import { toDxfText } from './dxf-text';

/** Height of a label on paper, millimetres (ГОСТ 2.304 size 2.5) — the screen sets labels at 10 px ≈ 2.6 mm. */
export const DXF_LABEL_TEXT_MM: number = 2.5;
/** Height of a large label on paper, millimetres (ГОСТ 2.304 size 3.5). */
export const DXF_LARGE_TEXT_MM: number = 3.5;
/** A screen font this size or larger, pixels, is a large label. */
export const LARGE_FONT_MIN_PX: number = 12;
/** The line spacing of the ANSI31 pattern at scale 1, drawing units. */
export const ANSI31_SPACING: number = 3.175;

const FULL_TURN: number = Math.PI * 2;
const RAD_TO_DEG: number = 180 / Math.PI;

const H_ALIGN: Record<ETextAlign, EDxfHorizontalAlign> = {
    [ETextAlign.Left]: EDxfHorizontalAlign.Left,
    [ETextAlign.Center]: EDxfHorizontalAlign.Center,
    [ETextAlign.Right]: EDxfHorizontalAlign.Right,
};

const V_ALIGN: Record<ETextBaseline, EDxfVerticalAlign> = {
    [ETextBaseline.Top]: EDxfVerticalAlign.Top,
    [ETextBaseline.Middle]: EDxfVerticalAlign.Middle,
    [ETextBaseline.Bottom]: EDxfVerticalAlign.Bottom,
};

function degrees(radians: number): number {
    const deg: number = (radians * RAD_TO_DEG) % 360;
    return deg < 0 ? deg + 360 : deg;
}

function layerOf(fit: IDxfPaneFit, kind: EDxfLayerKind): string {
    return paneLayerName(fit.paneId, kind);
}

function polyline(prim: IPolyPrimitive, fit: IDxfPaneFit, kind: EDxfLayerKind): IDxfEntity {
    // The Y flip mirrors the plane, so every bulge changes sign.
    const vertices: IDxfVertex[] = prim.points.map((at: IPoint, index: number): IDxfVertex => ({
        at: toPaneMm(fit, at),
        bulge: -(prim.bulges?.[index] ?? 0),
    }));
    return { vertices, kind: EDxfEntityKind.Polyline, layer: layerOf(fit, kind), ltScale: fit.scale, closed: prim.closed };
}

/**
 * A polyline of the screen: a stroked one on the layer of its line kind; a filled one with no
 * stroke as its outline — or, a filled triangle (an arrowhead), as a solid fill. The screen's
 * own extension lines are left out: every dimension of the file draws its own.
 */
function polyEntities(prim: IPolyPrimitive, fit: IDxfPaneFit): IDxfEntity[] {
    if (prim.points.length < 2) {
        return [];
    }
    if (prim.stroke !== undefined) {
        return isScreenExtensionLine(prim) ? [] : [polyline(prim, fit, strokeKind(prim))];
    }
    if (prim.fill === undefined) {
        return [];
    }
    if (prim.closed && prim.points.length === 3 && prim.fillAlpha === undefined) {
        return [
            {
                kind: EDxfEntityKind.Hatch,
                layer: layerOf(fit, EDxfLayerKind.Hatch),
                ltScale: fit.scale,
                boundary: prim.points.map((at: IPoint): IDxfPoint => toPaneMm(fit, at)),
                pattern: EDxfHatchPattern.Solid,
                patternScale: 1,
            },
        ];
    }
    return [polyline(prim, fit, fillOutlineKind(prim))];
}

function arcEntities(prim: IArcPrimitive, fit: IDxfPaneFit): IDxfEntity[] {
    if (prim.stroke === undefined && prim.fill === undefined) {
        return [];
    }
    const kind: EDxfLayerKind = prim.stroke === undefined ? fillOutlineKind(prim) : strokeKind(prim);
    const center: IDxfPoint = toPaneMm(fit, prim.center);
    const radius: number = prim.radius / fit.pxPerMm;
    const base: { layer: string; ltScale: number; center: IDxfPoint; radius: number } = {
        layer: layerOf(fit, kind),
        ltScale: fit.scale,
        center,
        radius,
    };
    if (Math.abs(prim.endAngle - prim.startAngle) >= FULL_TURN - 1e-9) {
        return [{ ...base, kind: EDxfEntityKind.Arc, startDeg: 0, endDeg: 360 }];
    }
    // Canvas angles turn clockwise on the screen; after the Y flip a canvas angle θ is −θ.
    // A canvas sweep in increasing angles therefore runs clockwise in the file, and a DXF
    // arc always runs counter-clockwise — so its ends swap.
    const [startDeg, endDeg]: [number, number] = prim.anticlockwise
        ? [degrees(-prim.startAngle), degrees(-prim.endAngle)]
        : [degrees(-prim.endAngle), degrees(-prim.startAngle)];
    return [{ ...base, kind: EDxfEntityKind.Arc, startDeg, endDeg }];
}

/** The opening: one ANSI31 hatch over its rectangle, its lines as far apart as the screen draws them. */
function hatchEntities(prim: IHatchPrimitive, fit: IDxfPaneFit): IDxfEntity[] {
    const { x, y, w, h }: { x: number; y: number; w: number; h: number } = prim.rect;
    const boundary: IDxfPoint[] = (
        [
            [x, y],
            [x + w, y],
            [x + w, y + h],
            [x, y + h],
        ] as const
    ).map((at: IPoint): IDxfPoint => toPaneMm(fit, at));
    return [
        {
            kind: EDxfEntityKind.Hatch,
            layer: layerOf(fit, EDxfLayerKind.Hatch),
            ltScale: fit.scale,
            pattern: EDxfHatchPattern.Ansi31,
            patternScale: prim.step / fit.pxPerMm / ANSI31_SPACING,
            boundary,
        },
    ];
}

function textEntities(prim: ITextPrimitive, fit: IDxfPaneFit): IDxfEntity[] {
    const text: string = toDxfText(prim.text);
    if (text.length === 0) {
        return [];
    }
    const paperHeight: number = prim.font.size >= LARGE_FONT_MIN_PX ? DXF_LARGE_TEXT_MM : DXF_LABEL_TEXT_MM;
    return [
        {
            kind: EDxfEntityKind.Text,
            layer: layerOf(fit, EDxfLayerKind.Text),
            ltScale: fit.scale,
            at: toPaneMm(fit, prim.at),
            height: paperHeight * fit.scale,
            // A canvas rotation turns clockwise on the screen; DXF turns counter-clockwise.
            rotationDeg: degrees(-(prim.rotation ?? 0)),
            hAlign: H_ALIGN[prim.align],
            vAlign: V_ALIGN[prim.baseline],
            widthFactor: 1,
            text,
        },
    ];
}

/** One drawing primitive (not a dimension) as DXF entities in the pane's own millimetres. */
export function primitiveToEntities(prim: Exclude<IPlanPrimitive, IDimPrimitive>, fit: IDxfPaneFit): IDxfEntity[] {
    switch (prim.kind) {
        case EPrimitiveKind.Poly:
            return polyEntities(prim, fit);
        case EPrimitiveKind.Arc:
            return arcEntities(prim, fit);
        case EPrimitiveKind.Hatch:
            return hatchEntities(prim, fit);
        case EPrimitiveKind.Text:
            return textEntities(prim, fit);
    }
}

/**
 * A pane's primitives as entities in its own millimetres: the drawing first, then the
 * dimensions, which find the points they measure in that drawing. A detail view, given the
 * `room` of its window, lays its dimensions out anew in rows around its parts by ГОСТ 2.307
 * (`detailEntities`); a view of the projection group keeps the sheet's places for them.
 */
export function paneEntities(primitives: readonly IPlanPrimitive[], fit: IDxfPaneFit, room: IDxfRoom | null = null): IDxfEntity[] {
    const dims: IDimPrimitive[] = [];
    const drawing: IDxfEntity[] = [];
    for (const prim of primitives) {
        if (prim.kind === EPrimitiveKind.Dim) {
            dims.push(prim);
        } else {
            drawing.push(...primitiveToEntities(prim, fit));
        }
    }
    const sketch: IDxfSketch = sketchOf(drawing);
    if (room) {
        return detailEntities(drawing, dims, fit, sketch, room);
    }
    return [...drawing, ...dims.flatMap((dim: IDimPrimitive): IDxfEntity[] => dimEntities(dim, fit, sketch))];
}
